/**
 * Admin auth context — parallel to AuthContext but backed by the admin_*
 * localStorage keys so an operator can stay signed in as both user and
 * admin at the same time. Uses storeSession / clearSession from api.js
 * to keep the access + refresh + user trio consistent.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  authAPI,
  adminAPI,
  storeSession,
  clearSession,
  getAccessToken,
} from '../services/api';

const AdminAuthContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);
  const [pendingEmail, setPendingEmail] = useState(null); // between login → OTP
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = getAccessToken('admin');
    const storedAdmin = localStorage.getItem('admin_user');
    if (storedToken && storedAdmin) {
      setToken(storedToken);
      setAdmin(JSON.parse(storedAdmin));
    }
    setLoading(false);
  }, []);

  const requestOtp = async (credentials) => {
    try {
      await authAPI.adminLogin(credentials);
      // Backend returns a generic 200 either way (anti-enumeration), so we
      // always advance to the OTP step and let the verify call reveal failure.
      setPendingEmail(credentials.email);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const verifyOtp = async (otp) => {
    if (!pendingEmail) {
      return { success: false, error: 'Start the login flow first.' };
    }
    try {
      const response = await authAPI.adminVerifyOtp({ email: pendingEmail, otp });
      const payload = response.data;
      const access_token  = payload.access_token || payload.token;
      const refresh_token = payload.refresh_token;
      const user          = payload.user;

      storeSession('admin', { access_token, refresh_token, user });
      setAdmin(user);
      setToken(access_token);
      setPendingEmail(null);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    // adminAPI.logout revokes the refresh token server-side, then wipes
    // the admin_* localStorage keys.
    await adminAPI.logout();
    clearSession('admin');
    setAdmin(null);
    setToken(null);
    setPendingEmail(null);
  };

  const isAuthenticated = () => !!token && !!admin;

  return (
    <AdminAuthContext.Provider value={{
      admin,
      token,
      pendingEmail,
      loading,
      requestOtp,
      verifyOtp,
      logout,
      isAuthenticated,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
};

export default AdminAuthContext;
